import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';

import Search from 'components/Common/Search';
import {
  MdContentCopy,
  MdOutlineCalendarToday,
  MdOutlineEdit,
  MdOutlineMoreVert,
  MdOutlineSchool,
  MdOutlineTextSnippet
} from 'react-icons/md';
import Button from 'components/Common/Button';
import { GoPlus } from 'react-icons/go';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import {
  useCloneKnowledgeBase,
  useDeleteKnowledgeBase,
  useGetKnowledgeBases
} from 'apis/business/setting/knowledgeBase/hook';
import KnowledgeBasesSkeleton from './KnowledgeBasesSkeleton';
import Grid from '@mui/material/Grid2';
import { formatDate } from 'utils/helperFunctions';
import NoResults from 'assets/NoResults';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { TbTextSize } from 'react-icons/tb';
import RetrievalTestingButton from '../DataSet/Components/RetrievalTestingButton';

export default function KnowledgeBases() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    id: number | null;
  }>({
    anchorEl: null,
    id: null
  });

  const [searchText, setSearchText] = useState<null | string>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setMenuState({ anchorEl: event.currentTarget, id });
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, id: null });
  };

  const { data: knowledgeBases, isLoading } = useGetKnowledgeBases(searchText);
  const { mutate: cloneKnowledgeBase } = useCloneKnowledgeBase();
  const { mutate: deleteKnowledgeBase } = useDeleteKnowledgeBase();
  const isKnowledgeBaseEmpty = !knowledgeBases?.data.length;
  const [open, setOpen] = useState<any>(false);

  const handleDelete = (id: number) => {
    deleteKnowledgeBase(String(id));
    handleClose();
  };

  if (isLoading) {
    return <KnowledgeBasesSkeleton />;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', lg: 'center' },
          gap: { xs: 2, lg: 0 }
        }}
      >
        <Typography variant='h6'>Knowledge Base</Typography>
        <Box display='flex' gap={1} width={{ xs: '100%', lg: 'auto' }} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Search
            size='small'
            sx={{ background: theme.palette.background.paper }}
            placeholder='Search by name'
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <Button
            onClick={() => navigate('add')}
            variant='contained'
            color='primary'
            startIcon={<GoPlus />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: { xs: 'nowrap', sm: 'normal' }
            }}
          >
            <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Create Knowledge Base
            </Box>
            <Box component='span' sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Create
            </Box>
          </Button>
        </Box>
      </Box>
      {isKnowledgeBaseEmpty ? (
        <EmptyStateView />
      ) : (
        <Grid container spacing={{ xs: 2, lg: 3 }} mt={{ xs: 2, lg: 3 }}>
          {knowledgeBases?.data.map((item, index) => (
            <Grid
              key={index}
              size={{ xs: 12, md: 6, lg: 4 }}
              bgcolor={theme.palette.background.paper}
              border='1px solid #ececec'
              borderRadius={2}
              padding={{ xs: 2, lg: 3 }}
            >
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Link to={String(item.id)}>
                  <Typography variant='h3' fontSize={18} fontWeight={600} color='primary'>
                    {item.name}
                  </Typography>
                </Link>
                <IconButton
                  id='basic-button'
                  aria-controls={menuState.id === item.id ? 'basic-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={menuState.id === item.id ? 'true' : undefined}
                  onClick={(event) => handleClick(event, item.id)}
                >
                  <MdOutlineMoreVert />
                </IconButton>
                <Menu
                  id={`basic-menu-${item.id}`}
                  anchorEl={menuState.anchorEl}
                  open={menuState.id === item.id}
                  onClose={handleClose}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button'
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: 150,
                        py: 0.5
                      }
                    }
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleClose;
                      navigate(`${item.id}/dataset`, {
                        state: {
                          from: window.location.href
                        }
                      });
                    }}
                  >
                    <ListItemIcon>
                      <MdOutlineEdit size={18} />
                    </ListItemIcon>
                    <ListItemText sx={{ mx: -1.5 }}>Edit</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleClose();
                      cloneKnowledgeBase(item.id);
                    }}
                  >
                    <ListItemIcon>
                      <MdContentCopy size={18} />
                    </ListItemIcon>
                    <ListItemText sx={{ mx: -1.5 }}>Duplicate</ListItemText>
                  </MenuItem>

                  <Link to={`${item.id}/edit`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <MenuItem onClick={handleClose}>
                      <ListItemIcon>
                        <TbTextSize size={18} />
                      </ListItemIcon>
                      <ListItemText sx={{ mx: -1.5 }}>Rename</ListItemText>
                    </MenuItem>
                  </Link>

                  <MenuItem
                    onClick={() => {
                      handleClose();
                      setOpen(item?.id);
                    }}
                  >
                    <ListItemIcon>
                      <MdOutlineSchool size={18} />
                    </ListItemIcon>
                    <ListItemText sx={{ mx: -1.5 }}>Test</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={() => handleDelete(item.id)} sx={{ color: 'error.main' }}>
                    <ListItemIcon sx={{ color: 'error.main' }}>
                      <RiDeleteBin6Line size={18} />
                    </ListItemIcon>
                    <ListItemText sx={{ mx: -1.5 }}>Delete</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
              <Typography color='secondary' variant='body2' marginBottom={3} marginTop={1}>
                {item.description}
              </Typography>
              <Box color={theme.palette.secondary.main} display='flex' gap={1} alignItems='center'>
                <MdOutlineTextSnippet />
                <Typography variant='body2' color='#000'>
                  {item.datasets?.length} Docs
                </Typography>
              </Box>
              <Box display='flex' gap={1} alignItems='center' marginTop={1}>
                <MdOutlineCalendarToday />
                <Typography variant='body2'>{formatDate(item.created_at)}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      <RetrievalTestingButton knowledgeBaseId={open} openModal={Boolean(open)} onClose={() => setOpen(false)} />
      <Outlet />
    </>
  );
}

const EmptyStateView = () => {
  return (
    <Box display='flex' flexDirection='column' alignItems='center' marginY={{ xs: 8, lg: 14 }}>
      <NoResults />
      <Typography color='secondary' marginTop={2}>
        No Data Found
      </Typography>
    </Box>
  );
};
